import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RotateCcw, Archive, GitMerge, ChevronDown, ChevronRight, Link2, Mail, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

function SiblingRow({ m, s, names, setNames, busy, restore }) {
  const key = `${m.log_id}:${s.id}`;
  const name = s.has_snapshot ? (s.asset_name || '') : (names[key] ?? s.asset_name ?? '');
  return (
    <li className="flex items-center justify-between gap-3 py-2" data-testid={`recovery-sibling-${s.id}`}>
      <div className="min-w-0 flex-1">
        {s.has_snapshot ? (
          <div className="text-sm font-semibold text-slate-800 truncate">{s.asset_name}{s.indication ? <span className="font-normal text-slate-500"> · {s.indication}</span> : null}</div>
        ) : (
          <Input value={name} onChange={e => setNames(n => ({ ...n, [key]: e.target.value }))} placeholder="Type the asset name to restore" className="h-8 text-sm font-semibold" data-testid={`recovery-name-input-${s.id}`} />
        )}
        <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`recovery-outreach-count-${s.id}`}>
          {s.outreach_count ? `${plural(s.outreach_count, 'sent email')} will move back to this asset` : 'No outreach history to move back'}
          {!s.has_snapshot && ' · older merge: the full profile was not kept, so only the name and merged fields return'}
        </div>
      </div>
      <Button size="sm" variant="outline" disabled={!!busy || !name.trim()} onClick={() => restore(key, { kind: 'merged', log_id: m.log_id, sibling_id: s.id, asset_name: name.trim() })} data-testid={`recovery-restore-merged-${s.id}`}>
        {busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Restore'}
      </Button>
    </li>
  );
}

function MergeGroup({ m, ...rest }) {
  return (
    <li className="rounded-lg border border-slate-200 p-3" data-testid={`recovery-merge-${m.log_id}`}>
      <div className="text-xs text-slate-600 mb-1">
        {fmt(m.merged_at)} · {plural(m.siblings.length, 'asset')} folded into <b className="text-slate-800">{m.primary_asset_name || 'an asset'}</b>
        {!m.primary_exists && <span className="ml-1 text-amber-700">(that asset has since been removed too)</span>}
      </div>
      <ul className="divide-y divide-slate-100">
        {m.siblings.map(s => <SiblingRow key={s.id} m={m} s={s} {...rest} />)}
      </ul>
    </li>
  );
}

function OutreachHistory({ companyId, onMoved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [targets, setTargets] = useState({});
  const [busy, setBusy] = useState('');

  const load = async (name = '') => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/biotech-companies/${companyId}/outreach-groups`, { params: name ? { name } : {}, withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load sent-email history');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (companyId) load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = async (g) => {
    const key = `${g.biotech_company_id}|${g.asset_id}|${g.asset_name}|${g.only_rewired ? 'r' : 'o'}|${g.inferred_asset || ''}|${g.manual ? 'm' : 'e'}`;
    const to = targets[key];
    if (!to) return;
    const toName = (data?.assets || []).find(a => a.id === to)?.asset_name || 'the selected asset';
    if (!window.confirm(`Move ${g.count} email record(s) labelled "${g.asset_name || '(no asset)'}" to ${toName}?`)) return;
    setBusy(key);
    try {
      const r = await axios.post(`${API}/biotech-companies/${companyId}/outreach-groups/move`, { from_asset_id: g.asset_id, from_asset_name: g.asset_name, from_company_id: g.biotech_company_id, only_rewired: !!g.only_rewired, inferred_asset: g.only_rewired ? (g.inferred_asset || '') : null, manual: !!g.manual, to_asset_id: to }, { withCredentials: true });
      toast.success(`${r.data.moved} email record(s) moved to ${r.data.to_asset_name}${r.data.marked_completed ? ' · marked Email: Completed' : ''}`, { duration: 8000 });
      await load(query);
      onMoved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    } finally {
      setBusy('');
    }
  };

  const [openRows, setOpenRows] = useState({});
  const [picked, setPicked] = useState({});
  const moveRows = async (g, key) => {
    const ids = Object.keys(picked[key] || {}).filter(id => picked[key][id]);
    const to = targets[key];
    if (!ids.length || !to) return;
    setBusy(key);
    try {
      const r = await axios.post(`${API}/biotech-companies/${companyId}/outreach-groups/move-rows`, { ids, to_asset_id: to }, { withCredentials: true });
      toast.success(`${r.data.moved} entr${r.data.moved === 1 ? 'y' : 'ies'} moved to ${r.data.to_asset_name}`, { duration: 8000 });
      setPicked(p => ({ ...p, [key]: {} }));
      await load(query);
      onMoved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    } finally {
      setBusy('');
    }
  };

  const groups = data?.groups || [];
  const assets = data?.assets || [];
  return (
    <section data-testid="recovery-outreach-history">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1"><Mail className="w-3.5 h-3.5" /> Where the sent emails are now</h4>
      <p className="text-[11px] text-slate-500 mb-2">Every email record for this company, grouped by the asset label it carries. If a group sits under the wrong (or a deleted) asset, move it to the right one.</p>
      <form className="flex items-center gap-2 mb-3" onSubmit={e => { e.preventDefault(); load(query); }}>
        <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by asset name across all companies (e.g. TPN171)" className="h-8 text-sm" data-testid="recovery-history-search" />
        <Button type="submit" size="sm" variant="outline" disabled={loading} data-testid="recovery-history-search-btn"><Search className="w-3.5 h-3.5" /></Button>
      </form>
      {loading && <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>}
      {!loading && data && groups.length === 0 && <p className="text-sm text-slate-500" data-testid="recovery-history-empty">No email records found{query ? ` for "${query}"` : ' for this company'}.</p>}
      <ul className="space-y-2">
        {groups.map(g => {
          const key = `${g.biotech_company_id}|${g.asset_id}|${g.asset_name}|${g.only_rewired ? 'r' : 'o'}|${g.inferred_asset || ''}`;
          const tid = `${g.asset_id || 'none'}${g.only_rewired ? '-rewired' : ''}${g.manual ? '-manual' : ''}${g.inferred_asset ? '-' + g.inferred_asset.replace(/\W+/g, '_') : ''}`;
          const needsMove = !g.asset_live || !g.this_company || g.only_rewired;
          return (
            <li key={key} className={`rounded-lg border p-3 ${needsMove ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`} data-testid={`history-group-${tid}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{g.asset_name || <i className="font-normal">no asset label</i>}{g.inferred_asset && g.inferred_asset !== 'several' && g.inferred_asset !== g.asset_name ? <span className="ml-2 text-xs font-medium text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">really {g.inferred_asset}?</span> : null}</div>
                  <div className="text-[11px] text-slate-600">{g.manual ? <b className="text-violet-700">Manual entries · </b> : null}{g.manual ? `${g.count} ${g.count === 1 ? 'entry' : 'entries'}` : plural(g.count, 'email record')} · {g.sent} sent · {plural(g.pharma_count, 'pharma')} · {fmt(g.first)}{g.last && g.last !== g.first ? ` → ${fmt(g.last)}` : ''}</div>
                  {g.pharmas.length > 0 && <div className="text-[11px] text-slate-500 truncate">{g.pharmas.join(', ')}{g.pharma_count > g.pharmas.length ? ', …' : ''}</div>}
                  <div className="text-[11px] mt-0.5">
                    {!g.this_company ? <span className="text-amber-700">Filed under another company: {g.company_name || g.biotech_company_id}</span>
                      : g.asset_live ? <span className="text-emerald-700">Linked to current asset {g.live_asset_name}</span>
                      : <span className="text-amber-700">Linked to a deleted asset — not visible on any asset right now</span>}
                    {g.rewired_from.length > 0 && <span className="text-slate-500"> · originally sent as {g.rewired_from.join(', ')}</span>}
                    {g.only_rewired && <span className="text-amber-700"> · re-labelled onto this asset by a merge</span>}
                    {g.only_rewired && g.rewired_from.length === 0 && (
                      g.inferred_asset && g.inferred_asset !== 'several'
                        ? <span className="font-semibold text-sky-800"> · the emails themselves talk about <u>{g.inferred_asset}</u></span>
                        : g.inferred_asset === 'several'
                          ? <span className="text-slate-500"> · emails mention several assets</span>
                          : <span className="text-slate-500"> · emails don't name an asset</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <select value={targets[key] || ''} onChange={e => setTargets(t => ({ ...t, [key]: e.target.value }))} className="h-8 text-xs rounded border border-slate-300 bg-white px-1.5 max-w-[160px]" data-testid={`history-move-select-${tid}`}>
                    <option value="">Move to…</option>
                    {assets.filter(a => a.id !== g.asset_id).map(a => <option key={a.id} value={a.id}>{a.asset_name}</option>)}
                  </select>
                  <Button size="sm" variant="outline" disabled={!targets[key] || !!busy} onClick={() => move(g)} data-testid={`history-move-btn-${tid}`}>
                    {busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Move'}
                  </Button>
                </div>
              </div>
              {g.rows?.length > 0 && (
                <div className="mt-2">
                  <button type="button" className="text-[11px] text-sky-700 hover:underline" onClick={() => setOpenRows(o => ({ ...o, [key]: !o[key] }))} data-testid={`history-rows-toggle-${tid}`}>
                    {openRows[key] ? 'Hide entries' : `Pick individual entries (${g.rows.length})`}
                  </button>
                  {openRows[key] && (
                    <div className="mt-1.5 rounded border border-slate-200 bg-white">
                      <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-100 text-[11px]">
                        <label className="flex items-center gap-1.5"><input type="checkbox" checked={g.rows.every(r => picked[key]?.[r.id])} onChange={e => setPicked(p => ({ ...p, [key]: Object.fromEntries(g.rows.map(r => [r.id, e.target.checked])) }))} data-testid={`history-rows-all-${tid}`} /> Select all</label>
                        <span className="text-slate-500">{Object.values(picked[key] || {}).filter(Boolean).length} selected</span>
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!targets[key] || !Object.values(picked[key] || {}).some(Boolean) || !!busy} onClick={() => moveRows(g, key)} data-testid={`history-rows-move-${tid}`}>Move selected</Button>
                      </div>
                      <ul className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                        {g.rows.map(r => (
                          <li key={r.id} className="flex items-start gap-2 px-2 py-1.5 text-[11px]" data-testid={`history-row-${r.id}`}>
                            <input type="checkbox" className="mt-0.5" checked={!!picked[key]?.[r.id]} onChange={e => setPicked(p => ({ ...p, [key]: { ...(p[key] || {}), [r.id]: e.target.checked } }))} />
                            <div className="min-w-0 flex-1">
                              <div className="text-slate-800"><b>{r.company || '—'}</b> · {r.contact_name || '—'}{r.designation ? `, ${r.designation}` : ''}</div>
                              <div className="text-slate-500">{r.status || '—'} · {fmt(r.created_at)}{r.notes ? ` · ${r.notes}` : ''}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function AssetRecoveryDialog({ open, onOpenChange, companyId, onRestored }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [names, setNames] = useState({});
  const [showAuto, setShowAuto] = useState(false);
  const [relinking, setRelinking] = useState(false);

  const relink = async () => {
    setRelinking(true);
    try {
      const r = await axios.post(`${API}/biotech-companies/${companyId}/relink-outreach`, {}, { withCredentials: true });
      const rl = r.data.relinked || {};
      const n = (r.data.outreach || 0) + (r.data.ai_mappings || 0) + (r.data.marked_completed || 0) + (rl.split_moved || 0) + (rl.split_unresolved || 0) + (rl.split_error ? 1 : 0);
      const bits = [];
      if (rl.split_moved) bits.push(`${rl.split_moved} entries re-tagged to the asset they were sent for`);
      if (r.data.outreach) bits.push(`${plural(r.data.outreach, 'entry')} re-attached`);
      if (r.data.marked_completed) bits.push(`${plural(r.data.marked_completed, 'asset')} marked Email: Completed`);
      if (rl.split_unresolved) bits.push(`${rl.split_unresolved} entries could not be placed automatically — pick them below`);
      if (rl.split_error) bits.push(`repair error: ${rl.split_error}`);
      toast[n ? 'success' : 'info'](n ? bits.join(' · ') : 'Nothing to fix automatically — every entry already sits on a current asset. Use "Pick individual entries" below to re-tag any that are under the wrong asset.', { duration: 10000 });
      if (n) load();
      if (n) onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-attach failed');
    } finally {
      setRelinking(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/biotech-companies/${companyId}/asset-recovery`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load recovery history');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open && companyId) { setShowAuto(false); load(); } }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (key, body) => {
    setBusy(key);
    try {
      const r = await axios.post(`${API}/biotech-companies/${companyId}/asset-recovery/restore`, body, { withCredentials: true });
      const re = r.data.reattached || {};
      const n = (re.outreach || 0) + (re.ai_mappings || 0);
      toast.success(`Restored "${r.data.asset_name}"${n ? ` · ${re.outreach || 0} sent emails and ${re.ai_mappings || 0} mappings moved back` : ''}${r.data.outreach_ambiguous ? ' · its outreach history stays on the merged asset (could not be separated safely)' : ''}${r.data.partial ? ' · please review the asset details' : ''}`, { duration: 10000 });
      await load();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy('');
    }
  };

  const archived = data?.archived || [];
  const manual = (data?.merged || []).filter(m => m.manual);
  const auto = (data?.merged || []).filter(m => !m.manual);
  const empty = !loading && data && archived.length === 0 && manual.length === 0 && auto.length === 0;
  const rowProps = { names, setNames, busy, restore };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="asset-recovery-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Recover assets</DialogTitle>
          <DialogDescription>Assets that were removed or merged for this company. Restoring brings the asset back, along with its sent emails and mappings.</DialogDescription>
        </DialogHeader>
        <div className="flex items-start justify-between gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="recovery-relink-box">
          <div className="text-[11px] text-sky-900">
            <b>Re-created an asset instead of restoring it?</b> Sent emails still carry the old asset name. This re-attaches them to the asset with the same name, so they show as sent here and in AI Studio.
          </div>
          <Button size="sm" variant="outline" disabled={relinking} onClick={relink} className="shrink-0" data-testid="recovery-relink-btn">
            {relinking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Link2 className="w-3.5 h-3.5 mr-1" /> Re-attach sent emails</>}
          </Button>
        </div>
        {loading && <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
        {empty && <p className="text-sm text-slate-500 py-4" data-testid="asset-recovery-empty">Nothing to recover — no assets have been removed or merged for this company.</p>}
        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-1">
          {open && <OutreachHistory companyId={companyId} onMoved={onRestored} />}
          {archived.length > 0 && (
            <section>
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1"><Archive className="w-3.5 h-3.5" /> Removed by Enrich-all ({archived.length})</h4>
              <p className="text-[11px] text-slate-500 mb-2">The document check judged these were not the company's own programs. The full asset was kept and comes back exactly as it was.</p>
              <ul className="space-y-2">
                {archived.map(a => (
                  <li key={a.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`recovery-archived-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{a.asset_name}</div>
                      <div className="text-[11px] text-slate-500">{a.indication || '—'} · removed {fmt(a.archived_at)}</div>
                      {a.reconcile_verdict?.evidence && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">Reason given: {a.reconcile_verdict.evidence}</div>}
                    </div>
                    <Button size="sm" variant="outline" disabled={!!busy} onClick={() => restore(a.id, { kind: 'archived', archive_id: a.id })} data-testid={`recovery-restore-archived-${a.id}`}>
                      {busy === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Restore'}
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {manual.length > 0 && (
            <section>
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1"><GitMerge className="w-3.5 h-3.5" /> Merged into another asset ({manual.length})</h4>
              <p className="text-[11px] text-slate-500 mb-2">Merged by you or by Enrich-all. Restoring splits the asset back out and moves its sent emails with it.</p>
              <ul className="space-y-3">{manual.map(m => <MergeGroup key={m.log_id} m={m} {...rowProps} />)}</ul>
            </section>
          )}
          {auto.length > 0 && (
            <section>
              <button type="button" onClick={() => setShowAuto(v => !v)} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-800" data-testid="recovery-toggle-auto">
                {showAuto ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Automatic duplicate clean-ups ({auto.length})
              </button>
              <p className="text-[11px] text-slate-500 mt-1 mb-2">Exact same-name duplicates folded together by the system. Usually nothing to restore here — the surviving asset already holds everything.</p>
              {showAuto && <ul className="space-y-3">{auto.map(m => <MergeGroup key={m.log_id} m={m} {...rowProps} />)}</ul>}
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
